import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDownIcon } from '@untitledui-icons/react/line'
import { api } from '@/lib/api'
import { SUPPORTED_LOCALES, setLocale, getLocale } from '@/i18n'
import { identityKeys } from '@/hooks/api/useIdentity'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'

interface LanguageSelectorProps {
  className?: string
  showLabel?: boolean
  variant?: 'default' | 'compact'
  /** Skip backend persistence — use on unauthenticated screens */
  guest?: boolean
}

/**
 * Language selector component that allows users to change their locale
 * Persists the choice to the backend and updates i18next
 */
export function LanguageSelector({
  className,
  showLabel = true,
  variant = 'default',
  guest = false,
}: LanguageSelectorProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const currentLocale = getLocale()

  // Find current locale info
  const currentLocaleInfo = SUPPORTED_LOCALES.find(
    (l) => l.code === currentLocale
  ) || SUPPORTED_LOCALES[0]

  // Mutation to update locale on backend (only when authenticated)
  const updateLocaleMutation = useMutation({
    mutationFn: async (locale: string) => {
      await api.post(`/me/change-locale?locale=${encodeURIComponent(locale)}`)
      return locale
    },
    onSuccess: (locale) => {
      setLocale(locale)
      queryClient.invalidateQueries({ queryKey: identityKeys.all })
      toast.success(t('resources.generic.profile_updated'))
    },
    onError: () => {
      toast.error(t('resources.generic.error'))
    },
  })

  const handleLocaleChange = (localeCode: string) => {
    if (localeCode !== currentLocale) {
      if (guest) {
        setLocale(localeCode)
      } else {
        updateLocaleMutation.mutate(localeCode)
      }
    }
    setIsOpen(false)
  }

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={!guest && updateLocaleMutation.isPending}
        className={cn(
          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
          'disabled:cursor-not-allowed disabled:opacity-50',
          variant === 'compact' && 'px-2 py-1.5'
        )}
      >
        <span className="text-base">{currentLocaleInfo.flag}</span>
        {showLabel && <span>{currentLocaleInfo.name}</span>}
        <ChevronDownIcon
          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop to close on click outside */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div
            className={cn(
              'absolute bottom-full right-0 z-50 mb-1 min-w-[160px] overflow-hidden rounded-lg border bg-white shadow-lg',
              'dark:border-gray-700 dark:bg-gray-800'
            )}
          >
            <div className="max-h-64 overflow-auto py-1">
              {SUPPORTED_LOCALES.map((locale) => (
                <button
                  key={locale.code}
                  type="button"
                  onClick={() => handleLocaleChange(locale.code)}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-sm',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                    locale.code === currentLocale &&
                      'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                  )}
                >
                  <span className="text-base">{locale.flag}</span>
                  <span
                    className={cn(
                      'text-gray-700 dark:text-gray-300',
                      locale.code === currentLocale &&
                        'text-brand-700 dark:text-brand-400'
                    )}
                  >
                    {locale.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default LanguageSelector
